import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardPanelCard from "../cards/DashboardPanelCard";
import { useConstants } from "../../hooks/UseConstants";

function formatDate(value, isAr) {
    if (!value) return isAr ? 'غير متوفر' : 'Unavailable';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return new Intl.DateTimeFormat(isAr ? 'ar-EG' : 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(parsed);
}

function CertificateVerifyPanel({ embedded = false, initialCode = '' }) {
    const host = `${process.env.REACT_APP_LOCAL_HOST}`;
    const { language } = useConstants();
    const isAr = language === 'ar';
    const [searchParams] = useSearchParams();
    const [code, setCode] = useState(initialCode || searchParams.get('code') || '');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const verify = useCallback(async (rawCode) => {
        const trimmed = String(rawCode || '').trim().toUpperCase();
        if (!trimmed) return;
        setLoading(true);
        setError('');
        setResult(null);
        try {
            const res = await fetch(`${host}/certificates/verify/${encodeURIComponent(trimmed)}`);
            const data = await res.json();
            if (res.ok && data.data?.valid) {
                setResult(data.data);
                setCode(trimmed);
            } else {
                setError(data.errors?.[0] || data.message || (isAr ? 'الشهادة غير صالحة أو الرمز غير موجود' : 'Invalid or unknown certificate code'));
            }
        } catch {
            setError(isAr ? 'تعذر الاتصال بالخادم' : 'Could not reach server');
        }
        setLoading(false);
    }, [host, isAr]);

    useEffect(() => {
        const fromUrl = (initialCode || searchParams.get('code') || '').trim();
        if (!fromUrl) return;
        setCode(fromUrl.toUpperCase());
        verify(fromUrl);
        // Auto-verify once when opened via QR link or ?code=
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <DashboardPanelCard
            className="mx-0"
            title={embedded ? undefined : (isAr ? 'التحقق من الشهادة' : 'Verify certificate')}
            subtitle={embedded
                ? undefined
                : (isAr
                    ? 'أدخل رمز التحقق المطبوع على الشهادة أو امسح رمز QR'
                    : 'Enter the code printed on the certificate or scan its QR code')}
        >
            {!embedded ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <VerifiedOutlinedIcon color="primary" />
                    <Typography variant="body2" color="text.secondary">
                        {isAr
                            ? 'يمكن لأي شخص التحقق من صحة الشهادة باستخدام الرمز أو QR دون تسجيل دخول.'
                            : 'Anyone can verify a certificate using the code or QR without signing in.'}
                    </Typography>
                </Box>
            ) : null}
            <TextField
                fullWidth
                size="small"
                label={isAr ? 'رمز التحقق' : 'Verification code'}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') verify(code);
                }}
                inputProps={{ style: { letterSpacing: '0.12em', fontWeight: 600 } }}
                sx={{ mb: 2 }}
            />
            <Button variant="contained" fullWidth onClick={() => verify(code)} disabled={loading || !code.trim()}>
                {loading ? <CircularProgress size={22} color="inherit" /> : (isAr ? 'تحقق الآن' : 'Verify now')}
            </Button>
            {error ? <Typography color="error" sx={{ mt: 2 }}>{error}</Typography> : null}
            {result ? (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.35)' }}>
                    <Typography fontWeight={700} color="success.dark" sx={{ mb: 1 }}>
                        {isAr ? 'شهادة صالحة ومسجلة في المنصة' : 'Valid certificate registered on the platform'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{isAr ? 'الطالب:' : 'Student:'}</strong> {result.student_name || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{isAr ? 'الدورة:' : 'Course:'}</strong> {result.course_name || '—'}
                    </Typography>
                    {result.teacher_name ? (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>{isAr ? 'المدرس:' : 'Instructor:'}</strong> {result.teacher_name}
                        </Typography>
                    ) : null}
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>{isAr ? 'تاريخ الإصدار:' : 'Issued at:'}</strong> {formatDate(result.issued_at, isAr)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, letterSpacing: '0.1em' }}>
                        {isAr ? 'رمز التحقق:' : 'Verification code:'} {result.verification_code}
                    </Typography>
                </Box>
            ) : null}
        </DashboardPanelCard>
    );
}

export default CertificateVerifyPanel;
